import React, { useState, useRef, useEffect } from "react";
import useFocusTrap from "../../hooks/useFocusTrap";
import { useTheme, Button } from "@mui/material";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { PaperClipIcon } from "@heroicons/react/24/outline";

const ImageModal = ({
  isOpen,
  onClose,
  onSubmit,
  mode = "add", // "add" or "edit"
  imageUrl,
  setImageUrl,
  imageFile,
  setImageFile,
  imagePreview,
  setImagePreview,
  imagePreviewError,
  setImagePreviewError,
  handleFileChange,
  handleImageUrlChange,
  imageAlignment,
  setImageAlignment,
}) => {
  const theme = useTheme();
  const modalRef = useFocusTrap(isOpen, onClose);
  const [activeTab, setActiveTab] = useState("upload");
  const fileInputRef = useRef(null);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [urlPreview, setUrlPreview] = useState(null);

  const isEditMode = mode === "edit";
  const title = isEditMode ? "Edit Image" : "Add Image";
  const submitButtonText = isEditMode ? "Update Image" : "Add Image";
  
  // Determine which preview to show based on active tab
  const currentPreview = activeTab === "upload" ? uploadPreview : urlPreview;

  const handleCancel = () => {
    setImageUrl("");
    setImageFile(null);
    setImagePreview(null);
    setImagePreviewError(false);
    setActiveTab("upload");
    setImageAlignment("center"); // Reset to default
    setUploadPreview(null);
    setUrlPreview(null);
    onClose();
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    // Only clear errors when switching tabs, don't clear anything else
    setImagePreviewError(false);
    // Don't clear files, URLs, or previews - keep both tabs' states separate
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith("image/")) {
      const syntheticEvent = {
        target: { files: [file] },
      };
      handleFileChange(syntheticEvent);
    }
  };

  const handleFileDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // Initialize previews in edit mode when modal opens, and sync with file/URL changes
  useEffect(() => {
    if (isOpen && isEditMode) {
      // In edit mode, set previews based on what's available
      if (imageFile && imageFile.dataUrl) {
        // It's a base64 image from edit mode - show in upload tab
        setUploadPreview(imageFile.dataUrl);
      } else if (imageFile && imagePreview) {
        // New file uploaded in edit mode
        setUploadPreview(imagePreview);
      }
      
      if (imageUrl && imagePreview) {
        // URL image - show in URL tab
        setUrlPreview(imagePreview);
      } else if (imageUrl && !imagePreview && !imagePreviewError) {
        // URL entered but preview not loaded yet - keep existing preview if any
        // Don't clear it
      }
    } else if (isOpen && !isEditMode) {
      // In add mode, sync previews with imagePreview based on active tab
      if (imageFile && imagePreview) {
        // File uploaded - always set upload preview
        setUploadPreview(imagePreview);
      }
      if (imageUrl && imagePreview) {
        // URL loaded - always set URL preview
        setUrlPreview(imagePreview);
      }
    } else if (!isOpen) {
      // Reset previews when modal closes
      setUploadPreview(null);
      setUrlPreview(null);
    }
  }, [isOpen, isEditMode, imageFile, imageUrl, imagePreview, imagePreviewError]);

  if (!isOpen) return null;

  const canSubmit = (activeTab === "upload" ? (imageFile && uploadPreview) : (imageUrl && urlPreview)) && !imagePreviewError;

  return (
    <div
      className="fixed inset-0 flex items-start justify-center z-50 bg-black bg-opacity-70"
      onClick={handleCancel}
    >
      <div className="w-full flex justify-center items-start mt-[70px]">
        <div
          ref={modalRef}
          className="modal-content relative w-full max-w-47rem bg-white rounded-lg shadow-xl max-h-[90vh] flex flex-col overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="image-modal-title"
        >
          {/* Modal Header */}
          <div
            className="modal-header flex justify-between items-center py-4 px-5 border-b"
            style={{
              backgroundColor: theme.palette.primary.main,
              borderColor: theme.palette.secondary.v4,
            }}
          >
            <h2 id="image-modal-title" className="font-semibold">
              {title}
            </h2>
            <button
              onClick={handleCancel}
              aria-label="Close modal"
              className="inline-flex items-center justify-center rounded-full bg-black text-white h-6 w-6 hover:bg-gray-800 transition-colors"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b" style={{ borderColor: theme.palette.secondary.v4 }}>
            <button
              onClick={() => handleTabChange("upload")}
              className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
                activeTab === "upload" ? "border-b-2" : ""
              }`}
              style={{
                color: activeTab === "upload" ? theme.palette.info.main : theme.palette.text.secondary,
                borderBottomColor: activeTab === "upload" ? theme.palette.info.main : "transparent",
                backgroundColor: theme.palette.primary.main,
              }}
            >
              Upload
            </button>
            <button
              onClick={() => handleTabChange("url")}
              className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
                activeTab === "url" ? "border-b-2" : ""
              }`}
              style={{
                color: activeTab === "url" ? theme.palette.info.main : theme.palette.text.secondary,
                borderBottomColor: activeTab === "url" ? theme.palette.info.main : "transparent",
                backgroundColor: theme.palette.primary.main,
              }}
            >
              URL
            </button>
          </div>

          {/* Tab Content */}
          <div className="flex flex-col flex-grow overflow-hidden">
            <div className="flex flex-col px-5 sm:px-6 py-6 gap-5 flex-grow overflow-y-auto">
              {/* Image Alignment Buttons */}
              <div>
                <label
                  className="block text-sm font-medium mb-2"
                  style={{ color: theme.palette.text.main }}
                >
                  Image Alignment
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setImageAlignment("left")}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      imageAlignment === "left"
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-300"
                    }`}
                    style={
                      imageAlignment === "left"
                        ? {
                            backgroundColor: theme.palette.info.main,
                            color: theme.palette.text.light,
                          }
                        : {
                            backgroundColor: theme.palette.secondary.v4,
                            color: theme.palette.text.main,
                          }
                    }
                  >
                    Left
                  </button>
                  <button
                    type="button"
                    onClick={() => setImageAlignment("center")}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      imageAlignment === "center"
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-300"
                    }`}
                    style={
                      imageAlignment === "center"
                        ? {
                            backgroundColor: theme.palette.info.main,
                            color: theme.palette.text.light,
                          }
                        : {
                            backgroundColor: theme.palette.secondary.v4,
                            color: theme.palette.text.main,
                          }
                    }
                  >
                    Center
                  </button>
                  <button
                    type="button"
                    onClick={() => setImageAlignment("right")}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      imageAlignment === "right"
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-300"
                    }`}
                    style={
                      imageAlignment === "right"
                        ? {
                            backgroundColor: theme.palette.info.main,
                            color: theme.palette.text.light,
                          }
                        : {
                            backgroundColor: theme.palette.secondary.v4,
                            color: theme.palette.text.main,
                          }
                    }
                  >
                    Right
                  </button>
                </div>
              </div>

              {activeTab === "upload" ? (
                <>
                  {/* Upload from Computer */}
                  <div>
                    <label
                      className="block text-sm font-medium mb-2"
                      style={{ color: theme.palette.text.main }}
                    >
                      {isEditMode ? "Upload New Image" : "Upload from Computer"}{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      ref={fileInputRef}
                      id="image-file-input"
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        handleFileChange(e);
                      }}
                      className="hidden"
                    />
                    {!imageFile ? (
                      <div
                        onDrop={handleFileDrop}
                        onDragOver={handleFileDragOver}
                        className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors hover:border-blue-400"
                        style={{
                          borderColor: theme.palette.secondary.v4,
                        }}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <div className="flex flex-col items-center gap-2">
                          <PaperClipIcon
                            className="h-8 w-8"
                            style={{ color: theme.palette.text.main }}
                          />
                          <p className="text-sm" style={{ color: theme.palette.text.main }}>
                            Add an attachment
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div
                          className="flex justify-between items-center p-3 rounded-lg border"
                          style={{
                            borderColor: theme.palette.secondary.v4,
                            backgroundColor: theme.palette.primary.main,
                          }}
                        >
                          <span className="flex items-center gap-2 flex-1 min-w-0">
                            <span
                              className="text-sm truncate"
                              style={{ color: theme.palette.text.main }}
                            >
                              {imageFile.name || "Selected image"}
                            </span>
                          </span>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setImageFile(null);
                              setUploadPreview(null);
                              if (fileInputRef.current) {
                                fileInputRef.current.value = "";
                              }
                            }}
                            className="text-red-600 hover:text-red-800 transition-all px-2 flex-shrink-0"
                            aria-label="Remove file"
                          >
                            <span className="text-xl leading-none">&times;</span>
                          </button>
                        </div>
                      </div>
                    )}
                    <p className="text-xs mt-2" style={{ color: theme.palette.text.secondary }}>
                      Allowed: JPG, PNG, GIF, WebP
                    </p>
                  </div>

                  {/* Image Preview for upload tab */}
                  {uploadPreview && (
                    <div>
                      <label
                        className="block text-sm font-medium mb-2"
                        style={{ color: theme.palette.text.main }}
                      >
                        Preview
                      </label>
                      <div
                        className="border rounded-lg p-4"
                        style={{
                          borderColor: theme.palette.secondary.v4,
                          backgroundColor: theme.palette.primary.main,
                        }}
                      >
                        <img
                          src={uploadPreview}
                          alt="Preview"
                          className="max-w-full h-auto max-h-48 mx-auto rounded"
                        />
                        <p className="text-sm text-green-600 mt-2 text-center">
                          ✓ Image ready to {isEditMode ? "update" : "add"}
                        </p>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {/* Image URL Input */}
                  <div>
                    <label
                      htmlFor="image-url"
                      className="block text-sm font-medium mb-2"
                      style={{ color: theme.palette.text.main }}
                    >
                      Image URL <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="image-url"
                      type="url"
                      value={imageUrl}
                      onChange={(e) => {
                        handleImageUrlChange(e.target.value);
                        // Clear URL preview when URL changes
                        if (!e.target.value) {
                          setUrlPreview(null);
                        }
                      }}
                      className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      style={{
                        borderColor: theme.palette.secondary.v4,
                        color: theme.palette.text.main,
                      }}
                      placeholder="https://example.com/image.jpg"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && canSubmit) {
                          onSubmit();
                        }
                      }}
                    />
                  </div>

                  {/* URL Preview with validation */}
                  {imageUrl && !urlPreview && (
                    <div>
                      <label
                        className="block text-sm font-medium mb-2"
                        style={{ color: theme.palette.text.main }}
                      >
                        Preview (Loading...)
                      </label>
                      <div
                        className="border rounded-lg p-4"
                        style={{
                          borderColor: theme.palette.secondary.v4,
                          backgroundColor: theme.palette.primary.main,
                        }}
                      >
                        <img
                          src={imageUrl}
                          alt="Preview"
                          className="max-w-full h-auto max-h-48 mx-auto rounded"
                          onLoad={() => {
                            setUrlPreview(imageUrl);
                            setImagePreviewError(false);
                          }}
                          onError={() => {
                            setUrlPreview(null);
                            setImagePreviewError(true);
                          }}
                        />
                        <p className="text-sm text-gray-500 mt-2 text-center">
                          Loading preview...
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Image Preview for URL tab */}
                  {urlPreview && (
                    <div>
                      <label
                        className="block text-sm font-medium mb-2"
                        style={{ color: theme.palette.text.main }}
                      >
                        Preview
                      </label>
                      <div
                        className="border rounded-lg p-4"
                        style={{
                          borderColor: theme.palette.secondary.v4,
                          backgroundColor: theme.palette.primary.main,
                        }}
                      >
                        <img
                          src={urlPreview}
                          alt="Preview"
                          className="max-w-full h-auto max-h-48 mx-auto rounded"
                        />
                        <p className="text-sm text-green-600 mt-2 text-center">
                          ✓ Image ready to {isEditMode ? "update" : "add"}
                        </p>
                      </div>
                    </div>
                  )}



                  {/* Warning for Instagram/Facebook */}
                  {imageUrl &&
                    (imageUrl.includes("instagram.com") ||
                      imageUrl.includes("fbcdn.net") ||
                      imageUrl.includes("facebook.com")) && (
                      <div
                        className="p-3 rounded-md border"
                        style={{
                          backgroundColor: "#fffbeb",
                          borderColor: "#fde68a",
                          borderWidth: "1px",
                        }}
                      >
                        <p className="text-sm text-yellow-800">
                          <strong>Note:</strong> Instagram/Facebook images may not display due to
                          CORS restrictions. Consider downloading and uploading the image directly
                          instead.
                        </p>
                      </div>
                    )}
                </>
              )}
            </div>
          </div>

          {/* Error Messages */}
          {imagePreviewError && activeTab === "url" && (
            <div className="px-5 pb-0">
              <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded mb-2">
                Failed to load image. Please check the URL or try uploading a file instead.
              </div>
            </div>
          )}

          {/* Modal Footer */}
          <div
            className="w-full py-3 px-5 flex justify-end border-t gap-2"
            style={{ borderColor: theme.palette.secondary.v4 }}
          >
            <Button
              onClick={handleCancel}
              sx={{
                borderColor: theme.palette.info.main,
                color: theme.palette.info.main,
                textTransform: "none",
                backgroundColor: "transparent",
                border: "1px solid",
                "&:hover": {
                  backgroundColor: theme.palette.primary.main,
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={onSubmit}
              disabled={!canSubmit}
              sx={{
                backgroundColor: canSubmit
                  ? theme.palette.info.main
                  : theme.palette.secondary.v3,
                textTransform: "none",
                color: theme.palette.text.light,
                "&:disabled": {
                  opacity: 0.5,
                  cursor: "not-allowed",
                },
              }}
            >
              {submitButtonText}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageModal;
